import { Next, Play, Reset, Stop } from "assets/icons/Common";
import classNames from "classnames";
import { Button, Col, IconButtonInverted, Row } from "components/primitives";
import { useInterval } from "hooks/interval";
import { usePropChangedEffect } from "hooks/propChangedEffect";
import { useTTS } from "hooks/tts";
import { useCallback, useEffect, useState } from "react";
import Form from "react-bootstrap/esm/Form";
import styled from "styled-components";
import { WidgetFC } from "types/Widget";

export interface HundredsChartState {
  cellData: CellData[];
  countBy: number;
}

interface NumberCellStyleProps {
  width: string;
  x: number;
  y: number;
  backgroundColor: string;
  fontColor: string;
  selected: boolean;
}

interface CellData {
  color?: string;
  fontColor?: string;
}

const ColorPallete: { [key: string]: CellData } = {
  green: {
    color: "#7FDFDB",
    fontColor: "black",
  },
  pink: {
    color: "#FBBAD2",
    fontColor: "black",
  },
  orange: {
    color: "#FFB4A0",
    fontColor: "black",
  },
  purple: {
    color: "#B4ADE9",
    fontColor: "black",
  },
  blue: {
    color: "#80A0B6",
    fontColor: "black",
  },
  yellow: {
    color: "#FEE9A5",
    fontColor: "black",
  },
  light: {
    color: "white",
    fontColor: "var(--secondary)",
  },
  dark: {
    color: "var(--secondary)",
    fontColor: "var(--secondary)",
  },
};

export const HundredsChartWidget: WidgetFC<HundredsChartState> = ({
  state,
  setState,
  settings,
}) => {
  const { cellData, countBy } = state;
  const { start, rows, cols, order, countMethod } = settings;
  const [currentColor, setCurrentColor] = useState<string>("green");
  const [currentSelectedIndex, setCurrentSelectedIndex] = useState<number | undefined>();
  const [countByInternal, setCountByInternal] = useState(String(countBy));
  const { readAloud } = useTTS();

  const [setRunInterval, runInterval] = useInterval(
    () => {
      if (currentSelectedIndex !== undefined) {
        const increment = Number(countBy) || 1;
        const next = currentSelectedIndex + increment;
        if (next < rows * cols) {
          setCurrentSelectedIndex(next);
        } else {
          setRunInterval(false);
        }
      }
    },
    1500,
    false,
  );

  usePropChangedEffect(() => {
    // TODO: This wipes the cell data. Probably need to figure out
    // proper resizeing
    setState({ cellData: Array(rows * cols) });
  }, [rows, cols]);

  const getCellNumber = useCallback(
    (index: number) => {
      return order === "Descending" ? Number(start) - index : Number(start) + index;
    },
    [order, start],
  );

  useEffect(() => {
    setCurrentSelectedIndex(undefined);
  }, [start]);

  useEffect(() => {
    if (currentSelectedIndex !== undefined) {
      readAloud(String(getCellNumber(currentSelectedIndex)));
    }
  }, [currentSelectedIndex]);

  const cellSelected = (cellIndex: number) => {
    const newCellData = [...cellData];
    newCellData[cellIndex] = ColorPallete[currentColor];
    setState({ cellData: newCellData });
  };

  const colorSelected = (color: string) => {
    setCurrentColor(color);
  };

  const incrementIndex = () => {
    const increment = Number(countBy) || 1;
    if (currentSelectedIndex !== undefined) {
      const next = currentSelectedIndex + increment;
      if (next < rows * cols) {
        setCurrentSelectedIndex(next);
      } else {
        // Stop counting when maximum index is reached
        setRunInterval(false);
      }
    } else {
      setCurrentSelectedIndex(increment - 1);
    }
  };

  const countNextClicked = () => {
    if (countMethod === "Click to count") {
      incrementIndex();
    } else {
      if (!runInterval) {
        incrementIndex();
      }
      setRunInterval(!runInterval);
    }
  };

  const refreshClicked = () => {
    setCurrentSelectedIndex(undefined);
    setRunInterval(false);
    setState({ cellData: Array(rows * cols) });
  };

  const getCountIcon = () => {
    if (countMethod === "Click to count") {
      return Next;
    } else {
      return runInterval ? Stop : Play;
    }
  };

  const setCountBy = (value: string) => {
    const number = Number(value);
    if (!isNaN(number) && value !== "") {
      if (number > 0 && number <= rows * cols && Number.isInteger(number)) {
        setState({ countBy: number });
      }
    }
    setCountByInternal(value);
  };

  return (
    <OuterContainer>
      <ControlsContainer>
        <h4>Colours</h4>
        <ColorsContainer>
          {Object.keys(ColorPallete).map((key) => (
            <ColorButton
              key={key}
              backgroundColor={ColorPallete[key].color!}
              selected={key === currentColor}
              onClick={() => colorSelected(key)}
            />
          ))}
        </ColorsContainer>
        <ContainerBreak />
        <>
          <h4>Count By</h4>
          <CountByInput
            value={countByInternal}
            as="input"
            onChange={(e: React.ChangeEvent<any>) => setCountBy(e.target.value as any)}
            isInvalid={countBy !== Number(countByInternal)}
          />
          <ContainerBreak />
        </>
        <h4>
          {currentSelectedIndex === undefined || countMethod === "Automatic Counting"
            ? "Count"
            : "Next"}
        </h4>
        <CountingButton
          withShadow={false}
          Icon={getCountIcon()}
          onClick={countNextClicked}
          accentColor={runInterval ? "var(--danger)" : "var(--primary)"}
        />
        <ContainerBreak />
        <h4>Refresh</h4>
        <CountingButton
          style={{ padding: "2cqw" }}
          withShadow={false}
          Icon={Reset}
          accentColor="var(--danger)"
          onClick={refreshClicked}
        />
      </ControlsContainer>
      <NumberContainer>
        {Array(rows * cols)
          .fill(null)
          .map((_, index) => (
            <NumberCell
              key={index}
              onClick={() => cellSelected(index)}
              width={`${100 / Math.max(cols, rows)}%`}
              x={(index % cols) * (100 / Math.max(cols, rows))}
              y={Math.floor(index / cols) * (100 / Math.max(cols, rows))}
              backgroundColor={
                index < cellData?.length && cellData[index]?.color
                  ? cellData[index].color!
                  : "white"
              }
              fontColor={
                index < cellData?.length && cellData[index]?.fontColor
                  ? cellData[index].fontColor!
                  : "var(--secondary)"
              }
              selected={currentSelectedIndex === index}
            >
              {getCellNumber(index)}
            </NumberCell>
          ))}
      </NumberContainer>
    </OuterContainer>
  );
};

const OuterContainer = styled(Row)`
  width: 100%;
  height: 100%;
  padding: 5cqw 2cqw;
`;

const ControlsContainer = styled(Col)`
  margin: auto;
  height: 100%;
  text-align: center;
  align-items: center;

  h4 {
    color: var(--secondary);
    font-weight: 600;
    font-size: 3cqw;
    margin-bottom: 2cqh;
  }
`;

const ColorsContainer = styled(Row)`
  width: 65%;
  flex-wrap: wrap;
  gap: 1cqw;
  justify-content: center;
`;

const ColorButton = styled(Button)<{ backgroundColor: string; selected: boolean }>`
  background-color: ${(props) => props.backgroundColor};
  width: 45%;
  aspect-ratio: 1;
  border: ${(props) =>
    props.selected
      ? "0.4cqw solid #417192"
      : props.backgroundColor === "white"
        ? "0.4cqw solid var(--gray)"
        : "0.4cqw solid transparent"};
  box-shadow: ${(props) => (props.selected ? "0px 0px 3px 0px" : "none")};
`;

const CountingButton = styled(IconButtonInverted)`
  width: 45%;
`;

const NumberContainer = styled.div.attrs(() => ({
  className: "no-drag",
}))`
  aspect-ratio: 1;
  height: 100%;
  position: relative;
`;

const NumberCell = styled(Row).attrs<NumberCellStyleProps>((props) => ({
  style: {
    width: `calc(${props.width} + 1px)`,
    left: `calc(${props.x}% - 0.5px)`,
    top: `calc(${props.y}% - 0.5px)`,
    backgroundColor: props.selected ? "var(--primary)" : props.backgroundColor,
    color: props.selected ? "white" : props.fontColor,
  },
}))<NumberCellStyleProps>`
  aspect-ratio: 1;
  cursor: pointer;
  position: absolute;
  border: 0.3cqw solid var(--secondary);
  justify-content: center;
  align-items: center;
  font-size: 2.5cqw;
  font-weight: 600;
`;

const ContainerBreak = styled.div`
  margin-bottom: 3cqh;
  width: 100%;
`;

const CountByInput = styled(Form.Control).attrs<{ isInvalid: boolean }>((props) => ({
  className: classNames("form-control", "no-drag", props.isInvalid && "is-invalid"),
}))<{ isInvalid: boolean }>`
  width: 70%;
  border: 2px solid var(--secondary);
  text-align: center;
  font-size: 2.5cqw;
`;
